"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Ctx = {
  modules: string[];
  setModules: (mods: string[]) => void;
  hasModule: (code: string) => boolean;
  loading: boolean;
  error: string | null;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
};

const LicenseContext = createContext<Ctx>({
  modules: [],
  setModules: () => {},
  hasModule: () => false,
  loading: false,
  error: null,
  refresh: async () => {},
});

type Props = {
  children: React.ReactNode;
  /** Vom Server prefetchen und hier injizieren, um FOUC zu vermeiden */
  initialModules?: string[];
  /** API-Endpoint, der { modules: string[] } liefert */
  endpoint?: string;
  /** Autoload, wenn initialModules leer sind (default: true) */
  auto?: boolean;
  /** TTL in ms, nach der automatisch neu geladen wird (default: 5min) */
  ttlMs?: number;
};

export function LicenseProvider({
  children,
  initialModules = [],
  endpoint = "/api/license/modules",
  auto = true,
  ttlMs = 5 * 60 * 1000,
}: Props) {
  const [modules, setModules] = useState<string[]>(initialModules);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedAtRef = useRef<number>(initialModules.length ? Date.now() : 0);
  const inflightRef = useRef<Promise<void> | null>(null);
  const didAutoRef = useRef(false); // gegen Dev-StrictMode Doppel-Fetch

  const normalized = useMemo(
    () =>
      new Set(
        (modules ?? [])
          .map((m) => String(m).trim().toUpperCase())
          .filter(Boolean)
      ),
    [modules]
  );

  const hasModule = useCallback(
    (code: string) => normalized.has(String(code).trim().toUpperCase()),
    [normalized]
  );

  const doFetch = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(endpoint, { method: "GET", signal });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || `Fetch failed (${res.status})`);
        }
        const data = (await res.json()) as { modules?: string[] };
        setModules(data.modules ?? []);
        fetchedAtRef.current = Date.now();
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          setError(e?.message || "Unbekannter Fehler");
        }
      } finally {
        setLoading(false);
      }
    },
    [endpoint]
  );

  /** Öffentliches Refresh mit Deduplizierung und TTL-Bypass */
  const refresh = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      // TTL-Check
      if (!force && fetchedAtRef.current) {
        const age = Date.now() - fetchedAtRef.current;
        if (age < ttlMs) return;
      }
      // Dedupe
      if (inflightRef.current) return inflightRef.current;
      const ctrl = new AbortController();
      const p = doFetch(ctrl.signal).finally(() => {
        inflightRef.current = null;
      });
      inflightRef.current = p;
      return p;
    },
    [doFetch, ttlMs]
  );

  // Auto-Fetch beim ersten Mount (wenn nichts vorliegt)
  useEffect(() => {
    if (!auto) return;
    if (didAutoRef.current) return; // schützt vor Doppel-Fetch (StrictMode)
    didAutoRef.current = true;

    const noModulesYet = (initialModules?.length ?? 0) === 0;
    if (noModulesYet) {
      refresh({ force: true });
    }
  }, [auto, initialModules, refresh]);

  return (
    <LicenseContext.Provider
      value={{ modules, setModules, hasModule, loading, error, refresh }}
    >
      {children}
    </LicenseContext.Provider>
  );
}

export const useLicenseContext = () => useContext(LicenseContext);
